import * as styles from "./Field.css";

import { clsx } from "clsx";
import { type JSX, useMemo, useRef, useState } from "react";

import { Field as BaseField } from "@base-ui/react/field";
import { ControlContext, makeControlContext } from "@frontend/contexts";

import {
  FieldContext,
  FieldErrorTooltipContext,
  fieldControl,
  makeFieldContext,
  useFieldContext,
} from "./Field.context";
import type { FieldProps } from "./Field.types";

export function Field({
  orientation,
  size,
  labelAlign,
  labelBlockAlign,
  errorDisplay,
  className,
  onPointerOver,
  onPointerOut,
  onFocus,
  onBlur,
  ...props
}: FieldProps): JSX.Element {
  const inherited = useFieldContext();
  const resolvedOrientation = orientation ?? inherited.orientation;
  const resolvedSize = size ?? inherited.size;
  const resolvedLabelAlign = labelAlign ?? inherited.labelAlign;
  const resolvedLabelBlockAlign = labelBlockAlign ?? inherited.labelBlockAlign;
  const resolvedErrorDisplay = errorDisplay ?? inherited.errorDisplay;

  const rootRef = useRef<HTMLDivElement>(null);

  const [hovered, setHovered] = useState(false);
  const [held, setHeld] = useState(false);

  const floating = "tooltip" === resolvedErrorDisplay;

  const tooltip = useMemo(
    () => ({ anchor: rootRef, active: hovered || held }),
    [hovered, held],
  );

  const leaving = (root: HTMLDivElement, next: EventTarget | null) =>
    !(next instanceof Node) || !root.contains(next);

  const onControl = (target: EventTarget | null) => {
    const control = fieldControl(rootRef.current);

    return (
      control instanceof Node &&
      target instanceof Node &&
      control.contains(target)
    );
  };

  const root = (
    <BaseField.Root
      ref={rootRef}
      data-slot="field"
      className={clsx(
        styles.field({
          orientation: resolvedOrientation,
          grouped: inherited.grouped,
        }),
        className,
      )}
      onPointerOver={(event) => {
        onPointerOver?.(event);
        setHovered(onControl(event.target));
      }}
      onPointerOut={(event) => {
        onPointerOut?.(event);
        setHovered(onControl(event.relatedTarget));

        if (leaving(event.currentTarget, event.relatedTarget)) {
          setHeld(false);
        }
      }}
      onFocus={(event) => {
        onFocus?.(event);
        setHeld(true);
      }}
      onBlur={(event) => {
        onBlur?.(event);

        if (leaving(event.currentTarget, event.relatedTarget)) {
          setHeld(false);
        }
      }}
      {...props}
    />
  );

  return (
    <FieldContext.Provider
      value={makeFieldContext({
        orientation: resolvedOrientation,
        size: resolvedSize,
        labelAlign: resolvedLabelAlign,
        labelBlockAlign: resolvedLabelBlockAlign,
        errorDisplay: resolvedErrorDisplay,
      })}
    >
      <ControlContext.Provider
        value={makeControlContext({ size: resolvedSize })}
      >
        {floating ? (
          <FieldErrorTooltipContext.Provider value={tooltip}>
            {root}
          </FieldErrorTooltipContext.Provider>
        ) : (
          root
        )}
      </ControlContext.Provider>
    </FieldContext.Provider>
  );
}
